// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents, useMap } from 'react-leaflet';
import type { LeafletMouseEvent } from 'leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

interface MapSearchProps {
  onLocationChange: (lat: number, lng: number, radius: number) => void;
  initialLat?: number;
  initialLng?: number;
  initialRadius?: number;
}

// Component to handle map clicks and marker dragging
function MapClickHandler({ onLocationChange }: { onLocationChange: (lat: number, lng: number) => void }) {
  useMapEvents({
    click: (e: LeafletMouseEvent) => {
      onLocationChange(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Component to update map view
function MapUpdater({ center }: { center: [number, number] }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
}

const MapSearch: React.FC<MapSearchProps> = ({
  onLocationChange,
  initialLat = 44.4938,
  initialLng = 11.3387,
  initialRadius = 1000,
}) => {
  const [position, setPosition] = useState<[number, number]>([initialLat, initialLng]);
  const [radius, setRadius] = useState(initialRadius);
  const markerRef = useRef<any>(null);

  // Update position when initialLat/initialLng change (from address search)
  useEffect(() => {
    setPosition([initialLat, initialLng]);
  }, [initialLat, initialLng]);

  // Update radius when initialRadius changes
  useEffect(() => {
    setRadius(initialRadius);
  }, [initialRadius]);

  useEffect(() => {
    onLocationChange(position[0], position[1], radius);
  }, [position, radius]);

  const handleMarkerDragEnd = () => {
    const marker = markerRef.current;
    if (marker != null) {
      const newPos = marker.getLatLng();
      setPosition([newPos.lat, newPos.lng]);
    }
  };

  const handleMapClick = (lat: number, lng: number) => {
    setPosition([lat, lng]);
  };

  const handleRadiusChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRadius(parseInt(e.target.value));
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Radius Slider */}
      <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f5f5f5', borderRadius: '4px' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
          Raggio di ricerca: <strong>{radius}m</strong> ({(radius / 1000).toFixed(1)}km)
        </label>
        <input
          type="range"
          min="100"
          max="5000"
          step="100"
          value={radius}
          onChange={handleRadiusChange}
          style={{
            width: '100%',
            height: '8px',
            borderRadius: '5px',
            background: '#ddd',
            outline: 'none',
            cursor: 'pointer',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
          <span>100m</span>
          <span>1km</span>
          <span>2.5km</span>
          <span>5km</span>
        </div>
      </div>

      {/* Map */}
      <div style={{ height: '400px', border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden' }}>
        {/* @ts-ignore - React-Leaflet v5 types */}
        <MapContainer
          center={position}
          zoom={14}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          {/* @ts-ignore - React-Leaflet v5 types */}
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <MapClickHandler onLocationChange={handleMapClick} />
          <MapUpdater center={position} />

          {/* @ts-ignore - React-Leaflet v5 types */}
          <Marker
            position={position}
            draggable={true}
            eventHandlers={{
              dragend: handleMarkerDragEnd,
            }}
            ref={markerRef}
          />

          {/* @ts-ignore - React-Leaflet v5 types */}
          <Circle
            center={position}
            radius={radius}
            pathOptions={{
              color: '#2196F3',
              fillColor: '#2196F3',
              fillOpacity: 0.2,
            }}
          />
        </MapContainer>
      </div>

      {/* Coordinates Display */}
      <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#666' }}>
        📍 Coordinate: {position[0].toFixed(6)}, {position[1].toFixed(6)}
      </div>
    </div>
  );
};

export default MapSearch;
